import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Logo from "../components/Logo";
import { ArrowRight, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("agent@demo.com");
  const [password, setPassword] = useState("Demo@1234");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name.split(" ")[0]}`);
      nav(loc.state?.from?.pathname || "/dashboard", { replace: true });
    } catch (e) {
      const msg = e.response?.data?.detail || "Invalid credentials";
      setErr(msg);
    } finally {
      setBusy(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogle = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-paper" data-testid="login-page">
      {/* Visual side */}
      <div className="hidden md:flex relative overflow-hidden border-r border-hairline bg-[#0B0B0B]">
        <img src="https://images.pexels.com/photos/16116150/pexels-photo-16116150.jpeg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#0B0B0B] via-[#0B0B0B]/85 to-transparent" />
        <div className="relative p-12 flex flex-col justify-between w-full">
          <Logo size="md" />
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-brand">Welcome back</p>
            <h1 className="font-display text-5xl font-black tracking-tight text-white mt-3 leading-[0.95]">
              Source the<br />world. <span className="text-brand">Faster.</span>
            </h1>
            <p className="text-white/70 mt-5 max-w-md">
              Sign in to access your live RFPs, bids, MICE quotes and escrow status.
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-paper">
        <div className="w-full max-w-md">
          <div className="md:hidden mb-6"><Logo size="md" /></div>
          <p className="overline">Sign in</p>
          <h2 className="font-display text-3xl font-bold text-ink mt-3">Access your dashboard</h2>
          <p className="text-sm text-muted mt-2">
            New here? <Link to="/register" className="text-brand hover:underline">Create an account</Link>
          </p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <label className="overline">Email</label>
              <input
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full mt-2 px-4 py-3"
                data-testid="login-email"
              />
            </div>
            <div>
              <label className="overline">Password</label>
              <input
                type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full mt-2 px-4 py-3"
                data-testid="login-password"
              />
            </div>
            {err && (
              <p className="text-sm text-red-600 flex items-center gap-2" data-testid="login-error">
                <AlertCircle className="w-4 h-4" /> {err}
              </p>
            )}
            <button type="submit" disabled={busy} className="btn-primary w-full justify-center" data-testid="login-submit">
              {busy ? "Signing in…" : (<>Sign in <ArrowRight className="w-4 h-4" /></>)}
            </button>
          </form>

          <div className="my-6 flex items-center gap-3">
            <span className="flex-1 h-px bg-hairline" />
            <span className="text-xs font-mono text-muted uppercase tracking-widest">or</span>
            <span className="flex-1 h-px bg-hairline" />
          </div>

          <button onClick={handleGoogle} className="btn-ghost w-full justify-center" data-testid="login-google">
            Continue with Google
          </button>

          <div className="mt-8 p-4 card-ledger text-xs text-muted font-mono">
            <p className="overline mb-2">Beta demo accounts</p>
            Agent → agent@demo.com / Demo@1234<br />
            DMC → dmc@demo.com / Demo@1234<br />
            Admin → admin@trawelldmc.com / Admin@123
          </div>
        </div>
      </div>
    </div>
  );
}
